import React, { useContext, useEffect, useState, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { Link, Outlet, useNavigate, useLocation, useParams } from "react-router-dom";
import { useFirestore } from "../firebase/FirestoreContext";
import { getDocs, collection, getDoc, doc, updateDoc, deleteDoc, Timestamp } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EnvContext } from "../../context/EnvContext";
import config from "../../config";
import { arrayMoveImmutable } from "array-move";
import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";

// Create a drag handle for the sortable rows.
const DragHandle = SortableHandle(() => <span style={{ cursor: "grab", padding: "0 8px" }}>☰</span>);

// Create a sortable table row.
const SortableItem = SortableElement(({ item, onRowClick, onFeaturedToggle }) => (
  <tr style={{ ...styles.clickableRow, width: "100%" }} onClick={() => onRowClick(item)}>
    {/* Drag handle cell */}
    <td style={{ ...styles.td, width: "40px" }}>
      <DragHandle />
    </td>
    <td style={{ ...styles.td, width: "120px" }}>{item.id.substring(0, 8).toUpperCase()}</td>
    <td style={styles.tdName}>
      {item.articleTitle} {item.title && `(${item.title})`}
    </td>
    <td style={{ ...styles.td, width: "80px" }}>
      <input
        type="checkbox"
        checked={item.featured || false}
        // Prevent row click when toggling the checkbox
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onFeaturedToggle(item, e.target.checked)}
      />
    </td>
  </tr>
));

// Create a sortable container for the table body.
const SortableList = SortableContainer(({ items, onRowClick, onFeaturedToggle }) => {
  return (
    <tbody>
      {items.map((item, index) => (
        <SortableItem key={`item-${item.id}`} index={index} item={item} onRowClick={onRowClick} onFeaturedToggle={onFeaturedToggle} />
      ))}
    </tbody>
  );
});

const Knowledge = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const environment = useContext(EnvContext);
  const db = useFirestore();
  const [knowledgeList, setKnowledgeList] = useState([]);

  const collectionPath = environment === "staging" ? config.enveironment.staging.collectionPath : config.enveironment.production.collectionPath;

  useEffect(() => {
    const fetchKnowledge = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, `${collectionPath}Help/Knowledge/DataBase`));
        // Optionally, sort by sortOrder here if your documents include that field
        const knowledgeArray = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        knowledgeArray.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

        setKnowledgeList(knowledgeArray);
      } catch (error) {
        console.error("Error fetching knowledge entries:", error);
      }
    };
    fetchKnowledge();
  }, [db, environment, location, collectionPath]);

  // Handle drag and drop sorting.
  const onSortEnd = async ({ oldIndex, newIndex }) => {
    const newList = arrayMoveImmutable(knowledgeList, oldIndex, newIndex);
    setKnowledgeList(newList);
    // Update the new sort order in Firestore for each document.
    newList.forEach(async (item, index) => {
      try {
        const knowledgeRef = doc(db, `${collectionPath}Help/Knowledge/DataBase`, item.id);
        await updateDoc(knowledgeRef, { sortOrder: index });
      } catch (error) {
        console.error("Error updating sort order:", error);
        toast.error("Error updating sort order");
      }
    });
  };

  // Navigate to edit page on row click.
  const handleRowClick = (item) => {
    navigate(`/main/knowledge/edit/${item.id}`);
  };

  // Update the "featured" status in the database.
  const handleFeaturedToggle = async (item, value) => {
    try {
      const knowledgeRef = doc(db, `${collectionPath}Help/Knowledge/DataBase`, item.id);
      await updateDoc(knowledgeRef, { featured: value });
      setKnowledgeList((prev) => prev.map((k) => (k.id === item.id ? { ...k, featured: value } : k)));
      toast.success("Featured updated successfully!");
    } catch (error) {
      console.error("Error updating featured", error);
      toast.error("Error updating featured!");
    }
  };

  return (
    <div>
      <style>
        {`
   .dragging-helper {
  width: 100% !important;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  opacity: 0.9;
}

/* Disable text selection during drag */
.no-select {
  -webkit-user-select: none; 
  -ms-user-select: none; 
  user-select: none;
}
  `}
      </style>
      <div style={styles.header}>
        <h2>{environment === "staging" && <span style={{ background: "#dae121", marginRight: 5 }}>Staging</span>} Knowledge</h2>
        <div style={styles.buttonGroup}>
          <Link to={`/main/knowledge/new`}>
            <button style={styles.button}>New Knowledge</button>
          </Link>
        </div>
      </div>

      <table style={styles.table}>
        <thead>
          <tr style={{ backgroundColor: "rgba(49,57,66,.1)" }} className="no-select">
            <th style={{ ...styles.th, width: "40px" }}>Sort</th>
            <th style={{ ...styles.th, width: "120px" }}>Knowledge ID</th>
            <th style={styles.th}>Title</th>
            <th style={{ ...styles.th, width: "80px" }}>Featured</th>
          </tr>
        </thead>
        <SortableList
          items={knowledgeList}
          onSortEnd={onSortEnd}
          onRowClick={handleRowClick}
          onFeaturedToggle={handleFeaturedToggle}
          useDragHandle
          helperClass="dragging-helper"
        />
      </table>

      <Outlet />
      <ToastContainer position="top-center" autoClose={5000} hideProgressBar />
    </div>
  );
};

const EditKnowledge = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const environment = useContext(EnvContext);
  const db = useFirestore();
  const storage = getStorage();
  const fileInputRef = useRef(null);

  const collectionPath = environment === "staging" ? config.enveironment.staging.collectionPath : config.enveironment.production.collectionPath;

  const [knowledgeData, setKnowledgeData] = useState({
    articleTitle: "",
    title: "",
    description: "",
    image: "",
    footer: false,
    customURL: "",
    createdAt: Timestamp.fromDate(new Date()),
  });

  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchKnowledgeData = async () => {
      if (id) {
        try {
          const docRef = doc(db, `${collectionPath}Help/Knowledge/DataBase`, id);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            setKnowledgeData(docSnap.data());
          } else {
            console.log("No such document!");
            navigate("/main/knowledge");
          }
        } catch (error) {
          console.error("Error fetching knowledge data:", error);
          toast.error("Failed to fetch knowledge data.");
          navigate("/main/knowledge");
        }
      } else {
        setKnowledgeData({
          articleTitle: "",
          title: "",
          description: "",
          image: "",
          footer: false,
          customURL: "",
          createdAt: Timestamp.fromDate(new Date()),
        });
      }
    };

    fetchKnowledgeData();
  }, [id, db, collectionPath, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let imageUrl = knowledgeData.image;

      if (selectedImage) {
        const storageRef = ref(storage, `Knowledge/${id || new Date().getTime().toString()}`);
        await uploadBytes(storageRef, selectedImage);
        imageUrl = await getDownloadURL(storageRef);
      }

      const updatedKnowledge = {
        ...knowledgeData,
        image: imageUrl,
        createdAt: knowledgeData.createdAt || Timestamp.fromDate(new Date()),
      };

      const knowledgeRef = doc(db, `${collectionPath}Help/Knowledge/DataBase`, id);
      await updateDoc(knowledgeRef, updatedKnowledge);
      toast.success("Knowledge updated successfully!");

      navigate("/main/knowledge");
    } catch (error) {
      toast.error("Error saving knowledge!");
      console.error("Error saving knowledge:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm("Are you sure you want to delete this knowledge entry?");
    if (!confirmed) return;

    try {
      const knowledgeRef = doc(db, `${collectionPath}Help/Knowledge/DataBase`, id);
      await deleteDoc(knowledgeRef);
      toast.success("Knowledge deleted successfully!");
      navigate("/main/knowledge");
    } catch (error) {
      toast.error("Error deleting knowledge!");
      console.error("Error deleting knowledge:", error);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setKnowledgeData((prev) => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div>
      <ToastContainer position="top-center" autoClose={5000} hideProgressBar />
      <h2 style={styles.title}>{id ? "Edit Knowledge" : "New Knowledge"}</h2>

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formElement}>
          <label style={styles.formLabel}>Article Title:</label>
          <input
            type="text"
            value={knowledgeData.articleTitle}
            onChange={(e) => setKnowledgeData({ ...knowledgeData, articleTitle: e.target.value })}
            style={styles.formInput}
          />
        </div>

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Title:</label>
          <input
            type="text"
            value={knowledgeData.title}
            onChange={(e) => setKnowledgeData({ ...knowledgeData, title: e.target.value })}
            style={styles.formInput}
          />
        </div>

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Image:</label>
          {knowledgeData.image && <img src={knowledgeData.image} alt="Header" style={styles.imagePreview} />}
          <input type="file" ref={fileInputRef} onChange={handleFileUpload} />
        </div>

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Description:</label>
          <ReactQuill
            value={knowledgeData.description}
            onChange={(value) => setKnowledgeData({ ...knowledgeData, description: value })}
            style={styles.formTextarea}
          />
        </div>

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Footer:</label>
          <input type="checkbox" checked={knowledgeData.footer} onChange={(e) => setKnowledgeData({ ...knowledgeData, footer: e.target.checked })} />
        </div>

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Custom URL:</label>
          <input
            type="text"
            value={knowledgeData.customURL}
            onChange={(e) => setKnowledgeData({ ...knowledgeData, customURL: e.target.value })}
            style={styles.formInput}
          />
        </div>

        <div style={styles.buttonGroup}>
          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </button>

          {id && (
            <button type="button" style={styles.deleteButton} onClick={handleDelete}>
              Delete
            </button>
          )}

          <button type="button" style={styles.cancelButton} onClick={() => navigate("/main/knowledge")}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

const styles = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px",
    backgroundColor: "white",
    borderBottom: "1px solid #ccc",
  },
  button: {
    padding: "0px",
    width: "120px",
    backgroundColor: "#313942",
    color: "white",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    height: "50px",
  },
  buttonGroup: {
    display: "flex",
    gap: "10px",
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
  },
  th: {
    border: "1px solid #d1d0d0",
    padding: "8px",
    textAlign: "left",
  },
  td: {
    border: "1px solid #d1d0d0",
    padding: "8px",
    color: "gray",
  },
  tdName: {
    border: "1px solid #d1d0d0",
    padding: "8px",
    cursor: "pointer",
    fontWeight: "bold",
  },
  clickableRow: {
    cursor: "pointer",
  },
  title: {
    fontWeight: "200",
    paddingBottom: "10px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "20px",
    border: "1px solid #ccc",
    borderRadius: "5px",
    backgroundColor: "#f9f9f9",
  },
  formElement: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  formLabel: {
    fontWeight: "bold",
    fontSize: "14px",
    color: "#4a4141",
  },
  formInput: {
    padding: "10px",
    fontSize: "16px",
    border: "1px solid #ccc",
    borderRadius: "5px",
  },
  formTextarea: {
    padding: "10px",
    fontSize: "16px",
    border: "1px solid #ccc",
    borderRadius: "5px",
    height: "100px",
    resize: "vertical",
  },
  imagePreview: {
    maxWidth: "200px",
    maxHeight: "150px",
    marginTop: "10px",
    borderRadius: "5px",
  },
  deleteButton: {
    backgroundColor: "red",
    color: "white",
    padding: "10px 20px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "14px",
  },
  cancelButton: {
    backgroundColor: "#ccc",
    color: "black",
    padding: "10px 20px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "14px",
  },
};

export default Knowledge;
export { EditKnowledge };
